import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { RatingDisplay } from "./RatingDisplay";
import { Button } from "../atoms/Button";
import { HandThumbUpIcon } from "@heroicons/react/24/outline";
import { HandThumbUpIcon as HandThumbUpSolidIcon } from "@heroicons/react/24/solid";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import type { ReviewsListProps } from "../types/ReviewsList.types";

export const ReviewsList: React.FC<ReviewsListProps> = ({
  reviews,
  currentUserId,
  onMarkHelpful,
  onUnmarkHelpful,
  onEdit,
  onDelete,
  markedHelpful = new Set(),
  loading = false,
  showActions = true,
}) => {
  const { t } = useTranslation();
  const [expandedReviews, setExpandedReviews] = useState<Set<string>>(
    new Set(),
  );

  const toggleExpand = (reviewId: string) => {
    setExpandedReviews((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffInDays === 0) return t("reviewsList.date.today");
    if (diffInDays === 1) return t("reviewsList.date.yesterday");
    if (diffInDays < 7)
      return t("reviewsList.date.daysAgo", { count: diffInDays });
    if (diffInDays < 30)
      return t("reviewsList.date.weeksAgo", {
        count: Math.floor(diffInDays / 7),
      });
    if (diffInDays < 365)
      return t("reviewsList.date.monthsAgo", {
        count: Math.floor(diffInDays / 30),
      });
    return date.toLocaleDateString();
  };

  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const shouldTruncate = (text: string): boolean => {
    return text.length > 300;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse bg-tiber-50 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 bg-tiber-200 rounded-full" />
              <div className="flex-1 space-y-3">
                <div className="h-4 bg-tiber-200 rounded w-1/4" />
                <div className="h-4 bg-tiber-200 rounded w-3/4" />
                <div className="h-4 bg-tiber-200 rounded w-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-tiber-400 mb-4">
          <svg
            className="mx-auto h-12 w-12"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-tiber-900 mb-1">
          {t("reviewsList.noReviews")}
        </h3>
        <p className="text-sm text-tiber-600">{t("reviewsList.beFirst")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {reviews.map((review) => {
        const isExpanded = expandedReviews.has(review.id);
        const commentText = review.comment || "";
        const truncatedText = commentText.slice(0, 300) + "...";
        const needsTruncation = shouldTruncate(commentText);
        const isMarkedHelpful = markedHelpful.has(review.id);
        const isOwnReview = currentUserId === review.client?.full_name; // This should use ID

        return (
          <div
            key={review.id}
            className="bg-white border border-tiber-200 rounded-lg p-6 hover:shadow-md transition-shadow"
          >
            {/* Header */}
            <div className="flex items-start gap-4 mb-4">
              {/* Avatar */}
              <div className="shrink-0">
                {review.client?.profile_image_url ? (
                  <img
                    src={review.client.profile_image_url}
                    alt={review.client.full_name}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-tiber-600 flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {getInitials(
                        review.client?.full_name || t("reviewsList.anonymous"),
                      )}
                    </span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-base font-semibold text-tiber-900">
                    {review.client?.full_name || t("reviewsList.anonymous")}
                  </h4>
                  {review.is_verified && (
                    <CheckBadgeIcon
                      className="h-5 w-5 text-tiber-500"
                      title={t("reviewsList.verifiedConsultation")}
                    />
                  )}
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <RatingDisplay
                    rating={review.rating}
                    size="sm"
                    showNumber={false}
                  />
                  <span className="text-sm text-tiber-500">
                    {formatDate(review.created_at)}
                  </span>
                </div>
              </div>

              {/* Actions for own review */}
              {isOwnReview && showActions && (
                <div className="flex gap-2">
                  {onEdit && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => onEdit(review.id)}
                    >
                      {t("common.edit")}
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => onDelete(review.id)}
                    >
                      {t("common.delete")}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Comment */}
            {commentText && (
              <div className="mb-4">
                <p className="text-tiber-700 leading-relaxed whitespace-pre-wrap">
                  {needsTruncation && !isExpanded ? truncatedText : commentText}
                </p>
                {needsTruncation && (
                  <button
                    onClick={() => toggleExpand(review.id)}
                    className="text-sm text-tiber-600 hover:text-tiber-700 font-medium mt-2"
                  >
                    {isExpanded
                      ? t("reviewsList.showLess")
                      : t("reviewsList.readMore")}
                  </button>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center gap-4 pt-4 border-t border-tiber-100">
              {/* Helpful Button */}
              {showActions && !isOwnReview && (
                <button
                  onClick={() => {
                    if (isMarkedHelpful && onUnmarkHelpful) {
                      onUnmarkHelpful(review.id);
                    } else if (!isMarkedHelpful && onMarkHelpful) {
                      onMarkHelpful(review.id);
                    }
                  }}
                  className={`
                    flex items-center gap-2 px-3 py-1.5 rounded-lg
                    transition-colors text-sm font-medium
                    ${
                      isMarkedHelpful
                        ? "bg-tiber-600 text-white"
                        : "bg-tiber-50 text-tiber-700 hover:bg-tiber-100"
                    }
                  `}
                >
                  {isMarkedHelpful ? (
                    <HandThumbUpSolidIcon className="h-4 w-4" />
                  ) : (
                    <HandThumbUpIcon className="h-4 w-4" />
                  )}
                  <span>{t("reviewsList.helpful")}</span>
                  {review.helpful_count > 0 && (
                    <span
                      className={`
                      ${isMarkedHelpful ? "text-white" : "text-tiber-600"}
                    `}
                    >
                      ({review.helpful_count})
                    </span>
                  )}
                </button>
              )}

              {/* Verified Badge */}
              {review.is_verified && (
                <span className="text-xs text-tiber-600 bg-tiber-50 px-3 py-1 rounded-full">
                  {t("reviewsList.verifiedConsultation")}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
